import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { FEATURES } from "@src/constants";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { IPlaylistTag } from "@src/types/playlistTag";
import httpApi from "@src/utils/httpApi";

type UsePlaylistTagsQueryParams = {
  enabled?: boolean;
  /**
   * Populate each tag's subtags (`subPlaylistTags`). Ignored while
   * `FEATURES.PLAYLIST_SUBTAGS_ENABLED` is off, because Strapi answers a
   * populate key with no matching relation with a 400 [ROVR-3982].
   */
  withSubtags?: boolean;
};

/**
 * The visible playlist tags (a.k.a. categories), in display order. Shared by the
 * submit-playlist popup and any other tag picker so the two populate variants
 * stay on separate cache keys.
 */
export const usePlaylistTagsQuery = ({
  enabled = true,
  withSubtags = false,
}: UsePlaylistTagsQueryParams = {}): UseQueryResult<IPlaylistTag[]> => {
  const populateSubtags = withSubtags && FEATURES.PLAYLIST_SUBTAGS_ENABLED;

  return useQuery({
    queryKey: [QUERY_KEYS.GET_PLAYLIST_CATEGORIES, { populateSubtags }],
    queryFn: async () =>
      await httpApi.get("/api/playlist-tags", {
        params: {
          filters: {
            visible: "*",
          },
          sort: {
            displayOrder: "asc",
          },
          populate: {
            icon: {
              fields: ["url", "alternativeText"],
            },
            ...(populateSubtags
              ? {
                  subPlaylistTags: {
                    fields: ["label", "description", "displayOrder"],
                    sort: {
                      displayOrder: "asc",
                    },
                  },
                }
              : {}),
          },
        },
      }),
    select: (response: AxiosResponse) => response.data.data as IPlaylistTag[],
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    enabled,
  });
};
